/**
 * RetentionRiskView - Displays retention risk analysis
 * Reads from sentimentReport.analysis.retention_risk (dept level)
 * or orgReport.analysis.org_retention_risk (org level)
 */
import React from 'react';
import {
    AlertTriangle, Users, Heart, TrendingDown, TrendingUp,
    Shield, Star, ArrowRight, Loader2, CheckCircle2, RefreshCw
} from 'lucide-react';

const RISK_COLORS = {
    low: '#34C759',
    moderate: '#FF9500',
    elevated: '#FF6B35',
    high: '#FF3B30',
};

function getRiskColor(level) {
    const l = (level || '').toLowerCase();
    if (l.includes('high')) return RISK_COLORS.high;
    if (l.includes('elevated')) return RISK_COLORS.elevated;
    if (l.includes('moderate')) return RISK_COLORS.moderate;
    return RISK_COLORS.low;
}

function RetentionRiskView({ sentimentReport, orgReport, isOrgLevel = false, loading, onRegenerate }) {
    if (loading) {
        return (
            <div className="rp-loading">
                <Loader2 className="rp-spinner-icon" size={24} />
                <p>Loading retention risk analysis...</p>
            </div>
        );
    }

    const retention = isOrgLevel
        ? orgReport?.analysis?.org_retention_risk
        : sentimentReport?.analysis?.retention_risk;

    // Check if report exists but is missing new fields (stale data)
    const hasReport = isOrgLevel ? !!orgReport?.analysis : !!sentimentReport?.analysis;

    if (!retention) {
        return (
            <div className="rp-empty-state">
                <div className="rp-empty-icon"><Heart size={32} /></div>
                <h3>{hasReport ? 'Report Needs Regeneration' : 'No Retention Risk Analysis Available'}</h3>
                <p>{hasReport 
                    ? 'This report was generated before retention risk analysis was available. Regenerate to unlock this feature.'
                    : `Generate a ${isOrgLevel ? 'organization' : 'department sentiment'} report first.`}
                </p>
                {onRegenerate && (
                    <button className="rp-btn rp-btn-primary" onClick={onRegenerate} style={{ marginTop: '1rem' }}>
                        <RefreshCw size={14} /> {hasReport ? 'Regenerate Report' : 'Generate Report'}
                    </button>
                )}
            </div>
        );
    }

    const riskLevel = isOrgLevel ? retention.overall_org_risk_level : retention.overall_risk_level;
    const riskColor = getRiskColor(riskLevel);

    return (
        <div className="retention-risk-view">
            {/* Risk Level Summary */}
            <div className="ds-stat-row" style={{ marginBottom: '1.5rem' }}>
                <div className="ds-stat-card" style={{ borderTop: `3px solid ${riskColor}` }}>
                    <div className="ds-stat-value" style={{ color: riskColor, textTransform: 'capitalize' }}>
                        {riskLevel || 'Unknown'}
                    </div>
                    <div className="ds-stat-label">Overall Retention Risk</div>
                </div>
                {!isOrgLevel && retention.career_growth_satisfaction && (
                    <div className="ds-stat-card">
                        <div className="ds-stat-value" style={{ fontSize: '0.85rem' }}>{retention.career_growth_satisfaction}</div>
                        <div className="ds-stat-label">Career Growth Satisfaction</div>
                    </div>
                )}
                {!isOrgLevel && (retention.flight_risk_signals || []).length > 0 && (
                    <div className="ds-stat-card">
                        <div className="ds-stat-value">{retention.flight_risk_signals.length}</div>
                        <div className="ds-stat-label">Flight Risk Signals</div>
                    </div>
                )}
            </div>

            {/* Retention Analysis */}
            <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                <div className="ds-section-header">
                    <h2><Heart size={18} style={{ marginRight: 8 }} /> Retention Analysis</h2>
                </div>
                <div className="ds-section-body">
                    <p className="retention-summary">
                        {isOrgLevel ? retention.org_retention_summary : retention.retention_analysis}
                    </p>

                    {!isOrgLevel && retention.competitive_positioning && (
                        <div className="retention-positioning">
                            <h4><Shield size={14} /> Competitive Positioning</h4>
                            <p>{retention.competitive_positioning}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Flight Risk Signals (dept level) */}
            {!isOrgLevel && (retention.flight_risk_signals || []).length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><AlertTriangle size={18} style={{ marginRight: 8 }} /> Flight Risk Signals</h2>
                    </div>
                    <div className="ds-section-body">
                        <div className="retention-signals">
                            {retention.flight_risk_signals.map((signal, i) => (
                                <div key={i} className="retention-signal-card" style={{ borderLeftColor: getRiskColor(signal.severity) }}>
                                    <div className="retention-signal-header">
                                        <span className="retention-signal-severity" style={{ backgroundColor: getRiskColor(signal.severity) }}>
                                            {signal.severity}
                                        </span>
                                    </div>
                                    <h4>{signal.signal}</h4>
                                    <p><strong>Affected:</strong> {signal.affected_segment}</p>
                                    <p className="retention-response"><strong>Recommended Response:</strong> {signal.recommended_response}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Dept Risk Ranking (org level) */}
            {isOrgLevel && (retention.department_risk_ranking || []).length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><Users size={18} style={{ marginRight: 8 }} /> Department Risk Ranking</h2>
                    </div>
                    <div className="ds-section-body">
                        <div className="retention-dept-ranking">
                            {retention.department_risk_ranking.map((dept, i) => (
                                <div key={i} className="retention-dept-card" style={{ borderLeftColor: getRiskColor(dept.risk_level) }}>
                                    <div className="retention-dept-header">
                                        <h4>{dept.department}</h4>
                                        <span className="retention-signal-severity" style={{ backgroundColor: getRiskColor(dept.risk_level) }}>
                                            {dept.risk_level}
                                        </span>
                                    </div>
                                    {dept.primary_drivers && (
                                        <div className="retention-drivers">
                                            <strong>Drivers:</strong>
                                            <ul>
                                                {dept.primary_drivers.map((d, j) => <li key={j}>{d}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {dept.recommended_priority_actions && (
                                        <div className="retention-actions">
                                            <strong>Priority Actions:</strong>
                                            <ul>
                                                {dept.recommended_priority_actions.map((a, j) => <li key={j}><ArrowRight size={12} /> {a}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Key Retention Drivers (dept level) */}
            {!isOrgLevel && (retention.key_retention_drivers || []).length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><Star size={18} style={{ marginRight: 8 }} /> Key Retention Drivers</h2>
                    </div>
                    <div className="ds-section-body">
                        <ul className="retention-drivers-list">
                            {retention.key_retention_drivers.map((driver, i) => (
                                <li key={i}><CheckCircle2 size={14} style={{ color: '#34C759' }} /> {driver}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Org-wide themes */}
            {isOrgLevel && (retention.org_wide_retention_themes || []).length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><TrendingDown size={18} style={{ marginRight: 8 }} /> Organization-Wide Themes</h2>
                    </div>
                    <div className="ds-section-body">
                        <ul className="retention-drivers-list">
                            {retention.org_wide_retention_themes.map((theme, i) => (
                                <li key={i}><AlertTriangle size={14} style={{ color: '#FF9500' }} /> {theme}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Recommended Actions */}
            {(retention.recommended_retention_actions || retention.strategic_retention_recommendations || []).length > 0 && (
                <div className="ds-section">
                    <div className="ds-section-header">
                        <h2><TrendingUp size={18} style={{ marginRight: 8 }} /> Recommended Actions</h2>
                    </div>
                    <div className="ds-section-body">
                        <div className="retention-actions-grid">
                            {(retention.recommended_retention_actions || retention.strategic_retention_recommendations || []).map((action, i) => {
                                const priority = action.priority || '';
                                const priorityColor = priority.includes('immediate') ? '#FF3B30' : priority.includes('near') ? '#FF9500' : '#007AFF';
                                return (
                                    <div key={i} className="retention-action-card" style={{ borderTopColor: priorityColor }}>
                                        <span className="retention-action-priority" style={{ backgroundColor: priorityColor }}>
                                            {priority}
                                        </span>
                                        <h4>{action.action || action.recommendation}</h4>
                                        <p>{action.expected_impact}</p>
                                        {isOrgLevel && action.departments_benefited && (
                                            <p className="retention-action-depts">
                                                <Users size={12} /> {action.departments_benefited.join(', ')}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RetentionRiskView;
